import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Colors
const COLORS = {
    SAFFRON: '#FF9933',
    WHITE: '#FFFFFF',
    GREEN: '#138808',
    NAVY: '#000080',
    SKY_BLUE: '#87CEEB',
};

// Independence Canvas Component
function IndependenceCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            let confetti = [];
            let sparkles = [];
            let kites = [];
            let chakras = [];
            let fighterJet = null;
            let textParticles = [];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // [HELPER FUNCTIONS - Factory Pattern]

            // --- 1. Confetti Factory (Replacing Snowflakes) ---
            const createConfetti = () => {
                const colors = [COLORS.SAFFRON, COLORS.WHITE, COLORS.GREEN];
                return {
                    x: Math.random() * canvas.width,
                    y: -10,
                    size: Math.random() * 8 + 4, // Rectangle shape
                    speed: Math.random() * 2 + 1,
                    drift: Math.random() * 1 - 0.5,
                    color: colors[Math.floor(Math.random() * colors.length)],
                    rotation: Math.random() * Math.PI * 2,
                    rotationSpeed: (Math.random() - 0.5) * 0.1,
                    opacity: Math.random() * 0.6 + 0.4,
                };
            };

            const updateConfetti = (c) => {
                c.rotation += c.rotationSpeed;
                c.x += Math.sin(c.rotation) * c.drift; // Paper fluttering effect
                c.y += c.speed;
                // Reset when off screen
                if (c.y > canvas.height) {
                    c.y = -10;
                    c.x = Math.random() * canvas.width;
                }
            };

            const drawConfetti = (c) => {
                ctx.save();
                ctx.globalAlpha = c.opacity;
                ctx.translate(c.x, c.y);
                ctx.rotate(c.rotation);
                ctx.fillStyle = c.color;
                ctx.fillRect(-c.size / 2, -c.size / 2, c.size, c.size * 0.6); // Paper shape
                ctx.restore();
            };

            // --- 2. Sparkle Factory (Replacing Christmas Lights) ---
            const createSparkle = (x, y) => ({
                x,
                y,
                size: Math.random() * 2 + 1,
                pulseSpeed: Math.random() * 0.05 + 0.02,
                pulse: Math.random() * Math.PI * 2,
            });

            const updateSparkle = (s) => {
                s.pulse += s.pulseSpeed;
            };

            const drawSparkle = (s) => {
                const intensity = Math.sin(s.pulse) * 0.5 + 0.5;
                ctx.save();
                ctx.globalAlpha = intensity;
                ctx.shadowBlur = 10;
                ctx.shadowColor = COLORS.WHITE;
                ctx.fillStyle = COLORS.WHITE;
                ctx.beginPath();
                ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            };

            // --- 3. Kite Factory (Replacing Gift Boxes) ---
            const createKite = (x, y, color) => ({
                x,
                y,
                color,
                size: 20,
                floatOffset: Math.random() * Math.PI * 2,
                floatSpeed: 0.02,
                sway: 0,
            });

            const updateKite = (k) => {
                k.floatOffset += k.floatSpeed;
                k.y -= 0.2; // Slowly float up
                k.sway = Math.sin(k.floatOffset) * 10;
                // Reset to bottom if goes too high
                if (k.y < -50) {
                    k.y = canvas.height + 50;
                    k.x = Math.random() * canvas.width;
                }
            };

            const drawKite = (k) => {
                ctx.save();
                ctx.translate(k.x + k.sway, k.y);
                ctx.rotate(Math.sin(k.floatOffset) * 0.1); // Slight tilt

                // String
                ctx.beginPath();
                ctx.moveTo(0, 10);
                ctx.quadraticCurveTo(5, 30, -5, 50);
                ctx.strokeStyle = 'rgba(255,255,255,0.3)';
                ctx.stroke();

                // Kite Diamond
                ctx.beginPath();
                ctx.moveTo(0, -15); // Top
                ctx.lineTo(12, 0);  // Right
                ctx.lineTo(0, 15);  // Bottom
                ctx.lineTo(-12, 0); // Left
                ctx.closePath();
                ctx.fillStyle = k.color;
                ctx.fill();

                // Kite Cross
                ctx.beginPath();
                ctx.moveTo(0, -15);
                ctx.lineTo(0, 15);
                ctx.moveTo(-12, 0);
                ctx.lineTo(12, 0);
                ctx.strokeStyle = 'rgba(0,0,0,0.1)';
                ctx.stroke();

                ctx.restore();
            };

            // --- 4. Chakra Particle Factory (Replacing Stars) ---
            const createChakraParticle = () => ({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * 8 + 5,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: Math.random() * 0.02 + 0.01,
                alpha: Math.random() * 0.3 + 0.1,
            });

            const updateChakraParticle = (c) => {
                c.rotation += c.rotationSpeed;
            };

            const drawChakraParticle = (c) => {
                ctx.save();
                ctx.globalAlpha = c.alpha;
                ctx.translate(c.x, c.y);
                ctx.rotate(c.rotation);

                ctx.strokeStyle = COLORS.NAVY;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(0, 0, c.size, 0, Math.PI * 2); // Outer rim
                ctx.stroke();

                // Spokes (Simplified)
                for (let i = 0; i < 8; i += 1) {
                    ctx.rotate(Math.PI / 4);
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.lineTo(0, c.size);
                    ctx.stroke();
                }

                ctx.restore();
            };

            // --- 5. Fighter Jet Factory (Replacing Santa Sleigh) ---
            const createFighterJet = () => ({
                x: -300,
                y: canvas.height * 0.15,
                speed: 5, // Fast speed
                visible: false,
                cooldown: 0,
            });

            const updateFighterJet = (jet) => {
                if (jet.cooldown > 0) {
                    jet.cooldown -= 1;
                    return;
                }

                if (!jet.visible && Math.random() < 0.005) {
                    jet.visible = true;
                    jet.x = -300;
                }

                if (jet.visible) {
                    jet.x += jet.speed;
                    if (jet.x > canvas.width + 300) {
                        jet.visible = false;
                        jet.cooldown = 400; // Wait before next flyby
                    }
                }
            };

            const drawFighterJet = (jet) => {
                if (!jet.visible) return;

                ctx.save();

                // Draw Smoke Trails (Tricolor)
                const trailLength = 200;
                const offsets = [-10, 0, 10]; // Y offsets for 3 trails
                const colors = [COLORS.SAFFRON, COLORS.WHITE, COLORS.GREEN];

                offsets.forEach((offset, i) => {
                    const gradient = ctx.createLinearGradient(jet.x - trailLength, 0, jet.x, 0);
                    gradient.addColorStop(0, 'rgba(0,0,0,0)');
                    gradient.addColorStop(1, colors[i]);

                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.moveTo(jet.x - trailLength, jet.y + offset + (Math.random() * 2));
                    ctx.lineTo(jet.x, jet.y + offset);
                    ctx.lineTo(jet.x - trailLength, jet.y + offset + 5);
                    ctx.fill();
                });

                // Draw Jet Silhouette (Simplified triangle shape)
                ctx.translate(jet.x, jet.y);
                ctx.fillStyle = '#222'; // Dark Grey Jet
                ctx.beginPath();
                ctx.moveTo(30, 0); // Nose
                ctx.lineTo(-20, 15); // Wing bottom
                ctx.lineTo(-20, -15); // Wing top
                ctx.closePath();
                ctx.fill();

                ctx.restore();
            };

            // --- 6. Text Particle Factory ---
            const createTextParticle = (x, y, char, index) => ({
                x,
                y: y - 100,
                targetY: y,
                char,
                delay: index * 12,
                time: 0,
                alpha: 0,
                scale: 0,
                color: [COLORS.SAFFRON, COLORS.WHITE, COLORS.GREEN][index % 3],
            });

            const updateTextParticle = (t) => {
                t.time += 1;
                if (t.time > t.delay) {
                    if (t.y < t.targetY) t.y += 8;
                    t.alpha = Math.min(1, t.alpha + 0.1);
                    t.scale = Math.min(1, t.scale + 0.1);
                }
            };

            const drawTextParticle = (t) => {
                if (t.time < t.delay) return;
                ctx.save();
                ctx.globalAlpha = t.alpha;
                ctx.translate(t.x, t.y);
                ctx.scale(t.scale, t.scale);

                ctx.font = 'bold 50px "Arial", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Shadow for glow
                ctx.shadowBlur = 15;
                ctx.shadowColor = t.color;

                // Fill text
                ctx.fillStyle = t.color;
                ctx.fillText(t.char, 0, 0);

                // Border for white text visibility
                if (t.color === COLORS.WHITE) {
                    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
                    ctx.lineWidth = 1;
                    ctx.strokeText(t.char, 0, 0);
                }

                ctx.restore();
            };

            // --- Initialization ---

            const initializeElements = () => {
                // Create Confetti
                confetti = [];
                for (let i = 0; i < 100; i += 1) {
                    confetti.push(createConfetti());
                }

                // Create Background Sparkles
                sparkles = [];
                for (let i = 0; i < 50; i += 1) {
                    sparkles.push(createSparkle(Math.random() * canvas.width, Math.random() * canvas.height));
                }

                // Create Background Chakras
                chakras = [];
                for (let i = 0; i < 15; i += 1) {
                    chakras.push(createChakraParticle());
                }

                // Create Kites
                kites = [];
                const kiteColors = [COLORS.SAFFRON, COLORS.GREEN, COLORS.SAFFRON, COLORS.GREEN];
                const positions = [
                    { x: canvas.width * 0.1, y: canvas.height * 0.8 },
                    { x: canvas.width * 0.9, y: canvas.height * 0.7 },
                    { x: canvas.width * 0.2, y: canvas.height * 0.6 },
                    { x: canvas.width * 0.8, y: canvas.height * 0.9 },
                ];

                positions.forEach((pos, i) => {
                    kites.push(createKite(pos.x, pos.y, kiteColors[i % kiteColors.length]));
                });

                // Create Jet
                fighterJet = createFighterJet();
            };

            // Create Text
            const initializeText = () => {
                const text = 'INDEPENDENCE DAY';
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.12;
                const charSpacing = canvas.width < 768 ? 25 : 40;
                const startX = centerX - (text.length * charSpacing) / 2;

                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    textParticles.push(createTextParticle(startX + i * charSpacing, centerY, text[i], i));
                }
            }

            initializeElements();
            setTimeout(initializeText, 50);

            // --- Animation Loop ---
            const animate = () => {
                ctx.fillStyle = 'rgba(0, 4, 40, 0.2)'; // Dark Navy trail effect
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw Background Elements
                sparkles.forEach(s => { updateSparkle(s); drawSparkle(s); });
                chakras.forEach(c => { updateChakraParticle(c); drawChakraParticle(c); });

                // Draw Kites
                kites.forEach(k => { updateKite(k); drawKite(k); });

                // Draw Jet (Flypast)
                if (fighterJet) {
                    updateFighterJet(fighterJet);
                    drawFighterJet(fighterJet);
                }

                // Draw Confetti (Foreground)
                confetti.forEach(c => { updateConfetti(c); drawConfetti(c); });

                // Draw Text
                textParticles.forEach(t => { updateTextParticle(t); drawTextParticle(t); });

                animationFrameId = requestAnimationFrame(animate);
            }
            animate();

            const handleResize = () => {
                resizeCanvas();
                initializeElements();
                if (textParticles.length > 0) initializeText();
            };
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 2,
            }}
        />
    );
}

// Main Theme Export (Kept as IndependenceTheme to match existing imports)
export default function IndependenceTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            background: `linear-gradient(180deg, ${COLORS.NAVY} 0%, #000000 100%)`,
            overflow: 'hidden',
        }}>
            <IndependenceCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(0,2,15,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha(COLORS.SAFFRON, 0.35)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.5)}`,
                    `0 8px 32px ${alpha(COLORS.SAFFRON, 0.15)}`,
                    `0 8px 32px ${alpha(COLORS.GREEN, 0.15)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                    background: `linear-gradient(90deg, ${COLORS.SAFFRON} 33%, ${COLORS.WHITE} 33%, ${COLORS.WHITE} 66%, ${COLORS.GREEN} 66%)`,
                    borderRadius: '3px 3px 0 0',
                },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5, mt: 1 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: `linear-gradient(90deg, ${COLORS.SAFFRON}, ${COLORS.GREEN}, ${COLORS.NAVY}, ${COLORS.SAFFRON})`,
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha(COLORS.SAFFRON, 0.5)}`,
                    }}>Happy Independence Day</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 2 }}><Logo /></Stack>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {cloneElement(children, { hideHelpLink: true })}
                </Box>
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha(COLORS.NAVY, 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.4)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

IndependenceTheme.propTypes = {
    children: PropTypes.node,
};
